import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { ImplantsViewDataChanged } from '@itero/itero-client-communication';
import { ImplantsViewDataRxApi } from '@modules/teeth-diagram/models/implants-view-data';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { INTUITIVE_INDICATION_BRIDGE_OFFSET, IntuitiveIndication } from '@modules/rx-intuitive-teeth-diagram/models/intuitive-indication';
import { ApplicationModes, SoftwareOptions } from '@shared/models/enums/enums';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { PopupService } from '@shared/services/popup.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { take } from 'rxjs/operators';
import { ImplantsViewAppParameter } from './models/implants-view-app-parameter';

@Injectable({ providedIn: 'root' })
export class ImplantsViewService {
	constructor(
		private readonly hostPlatformService: HostPlatformService,
		private readonly shellQuery: ShellQuery,
		private readonly popupService: PopupService,
		private readonly logger: LoggerService
	) {}

	shouldPreventImplantBasedSelection(unitType: number): boolean {
		// Only check if it's Implant Based and offline
		if (unitType !== (IntuitiveIndication.ImplantBased as number) || !this.hostPlatformService.isOfflineMode()) {
			return false;
		}

		return this.checkAndPreventIfNewImplantsView();
	}

	shouldPreventImplantBasedInBridgeSelection(unitTypeInBridge: number): boolean {
		const implantBasedBridgeUnitType = IntuitiveIndication.BridgeImplantBased - INTUITIVE_INDICATION_BRIDGE_OFFSET;

		// Only check if it's Implant Based and offline
		if (unitTypeInBridge !== implantBasedBridgeUnitType || !this.hostPlatformService.isOfflineMode()) {
			return false;
		}

		return this.checkAndPreventIfNewImplantsView();
	}

	/**
	 * RxLaunchViaSLOnScanner applies on scanner only; MIDC/web uses ITeroImplantsSystem alone.
	 * Returns false when required SWOs are missing or any tooth has legacy ImplantTypeID data.
	 */
	shouldShowNewImplantsView(companySoftwareOptions: number[], teeth: Tooth[]): boolean {
		const appMode = this.shellQuery.applicationMode as ApplicationModes;
		const requiredSoftwareOptions = HostPlatformService.isScanner(appMode)
			? [SoftwareOptions.ITeroImplantsSystem, SoftwareOptions.RxLaunchViaSLOnScanner]
			: [SoftwareOptions.ITeroImplantsSystem];

		if (!requiredSoftwareOptions.every(option => companySoftwareOptions.includes(option))) {
			return false;
		}

		if (teeth?.some((tooth: Tooth) => !!tooth?.ImplantTypeID)) {
			return false;
		}

		return true;
	}

	prepareImplantsViewPreselectedData(
		tooth: Tooth
	): Pick<
		ImplantsViewAppParameter,
		| 'toothNumber'
		| 'preselectedImplantManufacturerId'
		| 'preselectedImplantConnectionId'
		| 'preselectedImplantPlatformId'
		| 'preselectedScanBodyId'
	> {
		return {
			toothNumber: tooth.ToothID ?? 0,
			preselectedImplantManufacturerId: tooth?.ImplantsViewData?.ImplantManufacturerId,
			preselectedImplantConnectionId: tooth?.ImplantsViewData?.ImplantConnectionId,
			preselectedImplantPlatformId: tooth?.ImplantsViewData?.ImplantPlatformId,
			preselectedScanBodyId: tooth?.ImplantsViewData?.ScanBodyId
		};
	}

	parseImplantsViewEventToothId(toothNumber: number | string | null | undefined): number | null {
		if (toothNumber === undefined || toothNumber === null) {
			return null;
		}

		const n = typeof toothNumber === 'number' ? toothNumber : Number.parseInt(String(toothNumber), 10);

		return Number.isFinite(n) ? n : null;
	}

	parseImplantsViewDataChangedEvent(
		event: Maybe<ImplantsViewDataChanged>
	): { toothId: number; implantsViewData: ImplantsViewDataRxApi } | null {
		const data = event?.data;

		if (!data) {
			this.logger.warn('ImplantsViewDataChanged event has no data', { module: 'ImplantsViewService' });

			return null;
		}

		if (!data.toothNumber) {
			this.logger.warn('ImplantsViewDataChanged event data is missing toothNumber', { module: 'ImplantsViewService' });

			return null;
		}

		const toothId = this.parseImplantsViewEventToothId(data.toothNumber);

		if (toothId == null) {
			this.logger.warn('ImplantsViewDataChanged event data has invalid toothNumber', { module: 'ImplantsViewService' });

			return null;
		}

		// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unused-vars
		const { toothNumber: _tn, ...implantsPayload } = data;

		return {
			toothId,
			implantsViewData: {
				ImplantManufacturerId: implantsPayload.implantManufacturer?.id,
				ImplantManufacturerName: implantsPayload.implantManufacturer?.name,
				ImplantConnectionId: implantsPayload.implantConnection?.id,
				ImplantConnectionName: implantsPayload.implantConnection?.name,
				ImplantPlatformId: implantsPayload.implantPlatform?.id,
				ImplantPlatformName: implantsPayload.implantPlatform?.name,
				ScanBodyId: implantsPayload.scanBody?.id,
				ScanBodyName: implantsPayload.scanBody?.name,
				PartNumber: implantsPayload.scanBody?.partNumber,
				PartManufacturerName: implantsPayload.scanBody?.partManufacturerName,
				LibrarySuppliers: implantsPayload.scanBody?.librarySuppliers
			}
		};
	}

	checkIfNewImplantsViewRequired(): boolean {
		// Check if new implants view should be shown (both SWOs enabled and no old implant data)
		const rxConfiguration = this.shellQuery.rxConfiguration;
		const companySoftwareOptions = rxConfiguration?.CompanyConfiguration?.SoftwareOptionsForCompany ?? [];
		const teeth = this.shellQuery.getValue().rx?.Teeth;

		return this.shouldShowNewImplantsView(companySoftwareOptions, teeth ?? []);
	}

	private checkAndPreventIfNewImplantsView(): boolean {
		const shouldShowNewImplantsView = this.checkIfNewImplantsViewRequired();

		if (shouldShowNewImplantsView) {
			this.showOfflineModal();

			return true;
		}

		return false;
	}

	private showOfflineModal(): void {
		// Open the modal - the result is not needed, so we just call it
		this.popupService
			.openConfirmationPopUp({
				popUpInput: {
					titleTranslationKey: 'ToothEditor.OfflineModeTitle',
					contentTranslationKey: 'ToothEditor.OfflineModeMessage',
					hideCancelButton: true
				}
			})
			.pipe(take(1))
			.subscribe();
	}
}
